import { useMemo } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from 'react-icons/fi';
import { Button } from '../../../Button';
import { SelectField } from '../../../form';
import { Flex } from '../../../layout';
import { Text } from '../../../typography';
import { IsVisible } from '../../../utils';
import { Table } from '../index';

interface ITablePaginationProps {
  handleFirstPage?: () => void;
  handleLastPage?: () => void;
  handleNextPage?: () => void;
  handlePerPageLengthChange?: (value: number) => void;
  handlePrevPage?: () => void;
  isLastPage?: boolean;
  page: number;
  perPageLength?: number;
  rowsLabel?: string;
  rowsPerPage?: number[];
  totalPages?: number;
}

interface IRowsPerPage {
  label: number;
  value: number;
}

export function TablePagination({
  handleFirstPage,
  handleLastPage,
  handleNextPage,
  handlePerPageLengthChange,
  handlePrevPage,
  isLastPage = false,
  page,
  perPageLength = 0,
  rowsLabel = 'Rows per page:',
  rowsPerPage = [25, 50, 100, 250, 500],
  totalPages,
}: ITablePaginationProps) {
  const rowsPerPageFormatted = useMemo(() => {
    const options = rowsPerPage.map(rowPerPage => ({
      label: rowPerPage,
      value: rowPerPage,
    }));

    return options;
  }, [rowsPerPage]);

  const handleRowsPerPageChange = (value: IRowsPerPage) => {
    if (perPageLength !== Number(value.value)) {
      if (handlePerPageLengthChange) {
        handlePerPageLengthChange(Number(value.value));
      }
    }
  };

  return (
    <Table.Row>
      <Table.Cell colSpan={100}>
        <Flex className="max-w-fit ml-auto items-center justify-end gap-x-4">
          <IsVisible when={!!handlePerPageLengthChange}>
            <Flex className="items-center max-w-fit gap-x-2 mr-8">
              <Text className="text-gray-700 min-w-fit text-sm">
                {rowsLabel || 'Rows per page:'}
              </Text>
              <Flex className="max-w-fit">
                <SelectField
                  defaultValue={rowsPerPageFormatted[0]}
                  id="Pagination Select"
                  instanceId="Instance of Pagination Select"
                  onChange={value => {
                    const selectedOption = value as IRowsPerPage;
                    return handleRowsPerPageChange(selectedOption);
                  }}
                  options={[{ options: rowsPerPageFormatted }]}
                />
              </Flex>
            </Flex>
          </IsVisible>
          <Flex className="items-center max-w-fit h">
            <Text className="text-gray-700 text-sm">
              <IsVisible when={!!totalPages}>
                {`${page} of ${totalPages}`}
              </IsVisible>

              <IsVisible when={!totalPages}>{`Page nº ${page}`}</IsVisible>
            </Text>
          </Flex>
          <Flex>
            <Button
              className="h-8 px-0 text-gray-700 w-12"
              design="icon-only"
              disabled={page <= 1}
              onClick={handleFirstPage}
            >
              <FiChevronsLeft size={22} />
            </Button>
            <Button
              className="h-8 px-0 text-gray-700 w-12"
              design="icon-only"
              disabled={page <= 1}
              onClick={handlePrevPage}
            >
              <FiChevronLeft size={22} />
            </Button>
            <Button
              className="h-8 px-0 text-gray-700 w-12"
              design="icon-only"
              disabled={page === totalPages || isLastPage}
              onClick={handleNextPage}
            >
              <FiChevronRight size={22} />
            </Button>
            <IsVisible when={!!totalPages}>
              <Button
                className="h-8 px-0 text-gray-700 w-12"
                design="icon-only"
                disabled={page === totalPages}
                onClick={handleLastPage}
              >
                <FiChevronsRight size={22} />
              </Button>
            </IsVisible>
          </Flex>
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
}
